import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/api";
import {
  LayoutDashboard, Users, CalendarDays, ClipboardCheck, Star,
  Megaphone, Wallet, LogOut, Menu, X, IdCard, ShieldCheck, CalendarCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const items = [
  { to: "/admin",             label: "Dashboard",   icon: LayoutDashboard, end: true },
  { to: "/admin/alunos",      label: "Alunos",      icon: Users },
  { to: "/admin/turmas",      label: "Turmas",      icon: CalendarDays },
  { to: "/admin/calendario",  label: "Calendário",  icon: CalendarCheck2 },
  { to: "/admin/presenca",    label: "Presença",    icon: ClipboardCheck },
  { to: "/admin/avaliacoes",  label: "Avaliações",  icon: Star },
  { to: "/admin/avisos",      label: "Avisos",      icon: Megaphone },
  { to: "/admin/financeiro",  label: "Financeiro",  icon: Wallet },
  { to: "/admin/carteirinhas", label: "Carteirinhas", icon: IdCard },
  { to: "/admin/conta",       label: "Minha Conta", icon: ShieldCheck },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-card border-r border-border transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="admin-sidebar"
      >
        <div className="h-24 px-5 flex items-center gap-3 border-b border-border">
          <img src={BRAND.LOGO} alt="Vôlei Vida" className="h-16 w-auto vv-logo-glow" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Painel</div>
            <div className="font-bold text-lg vv-gradient-text">Administrador</div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Fechar menu">
            <X className="size-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-primary/10 text-foreground border border-primary/30"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <Icon className="size-4 text-[hsl(var(--vv-lime))]" />
              <span className="font-semibold tracking-wide uppercase text-xs">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 inset-x-0 p-4 border-t border-border bg-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="size-10 rounded-full bg-secondary/20 flex items-center justify-center font-bold text-secondary">
              {(user?.name || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="size-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-card border-b border-border h-14 flex items-center px-4 gap-3">
          <button onClick={() => setOpen(true)} aria-label="Abrir menu" data-testid="mobile-menu-open">
            <Menu className="size-6" />
          </button>
          <img src={BRAND.LOGO} alt="Vôlei Vida" className="h-10" />
          <span className="font-bold vv-gradient-text">Vôlei Vida</span>
        </header>

        <main className="p-5 lg:p-8 max-w-[1600px] mx-auto vv-fade-in">
          <Outlet />
        </main>
      </div>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
